import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import ReactDOM from 'react-dom';

const ToastContext = createContext(null);

const TOAST_CONFIG = {
  success: { accent: '#22C55E', iconBg: 'rgba(34,197,94,0.15)',  iconColor: '#4ADE80', icon: '✓' },
  error:   { accent: '#EF4444', iconBg: 'rgba(239,68,68,0.15)',  iconColor: '#F87171', icon: '✕' },
  warning: { accent: '#F59E0B', iconBg: 'rgba(245,158,11,0.15)', iconColor: '#FCD34D', icon: '⚠' },
  info:    { accent: '#3B82F6', iconBg: 'rgba(59,130,246,0.15)', iconColor: '#60A5FA', icon: 'ℹ' },
};

const AUTO_DISMISS_MS = 4000;
const MAX_VISIBLE = 5;

let toastId = 0;

const keyframesInjected = { current: false };

function injectKeyframes() {
  if (keyframesInjected.current) return;
  keyframesInjected.current = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes toast-in {
      from { opacity: 0; transform: translateX(100%); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes toast-out {
      from { opacity: 1; transform: translateX(0); }
      to   { opacity: 0; transform: translateX(100%); }
    }
    @media (max-width: 480px) {
      .toast-container-portal {
        left: 0 !important;
        right: 0 !important;
        padding: 8px !important;
      }
      .toast-item {
        max-width: 100% !important;
      }
    }
  `;
  document.head.appendChild(style);
}

function Toast({ toast, onClose }) {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef(null);
  const { accent, iconBg, iconColor, icon } = TOAST_CONFIG[toast.type] || TOAST_CONFIG.info;

  const isMultiLine = typeof toast.message !== 'string';

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onClose(toast.id), 300);
  }, [onClose, toast.id]);

  useEffect(() => {
    if (toast.persist) return;
    timerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timerRef.current);
  }, [dismiss, toast.persist]);

  const handleMouseEnter = () => clearTimeout(timerRef.current);
  const handleMouseLeave = () => {
    if (toast.persist) return;
    timerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
  };

  const itemStyle = {
    display: 'flex',
    alignItems: isMultiLine ? 'flex-start' : 'center',
    gap: '12px',
    padding: '14px 16px',
    marginBottom: '10px',
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    borderLeft: `4px solid ${accent}`,
    color: '#111111',
    fontSize: '14px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
    maxWidth: '460px',
    width: '100%',
    boxSizing: 'border-box',
    animation: exiting ? 'toast-out 0.3s ease forwards' : 'toast-in 0.3s ease',
    pointerEvents: 'auto',
  };

  const iconStyle = {
    flexShrink: 0,
    width: '26px',
    height: '26px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: iconBg,
    color: iconColor,
    fontSize: '13px',
    fontWeight: 'bold',
    lineHeight: 1,
    ...(isMultiLine ? { marginTop: '1px' } : {}),
  };

  const closeStyle = {
    marginLeft: 'auto',
    flexShrink: 0,
    background: 'none',
    border: 'none',
    color: '#9CA3AF',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0 0 0 8px',
    lineHeight: 1,
    transition: 'color 0.15s',
    ...(isMultiLine ? { marginTop: '1px' } : {}),
  };

  return (
    <div
      className="toast-item"
      role="alert"
      aria-live="polite"
      style={itemStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span style={iconStyle}>{icon}</span>
      <span style={{ flex: 1, wordBreak: 'break-word', lineHeight: '1.45' }}>{toast.message}</span>
      <button
        style={closeStyle}
        onClick={dismiss}
        aria-label="Close notification"
        onMouseEnter={e => (e.currentTarget.style.color = '#374151')}
        onMouseLeave={e => (e.currentTarget.style.color = '#9CA3AF')}
      >
        ×
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  useEffect(() => injectKeyframes(), []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((type, message, opts = {}) => {
    setToasts((prev) => {
      const next = [{ id: ++toastId, type, message, persist: opts.persist || false }, ...prev];
      return next.length > MAX_VISIBLE ? next.slice(0, MAX_VISIBLE) : next;
    });
  }, []);

  const api = useRef({
    success: (msg, opts) => addToast('success', msg, opts),
    error:   (msg, opts) => addToast('error', msg, opts),
    warning: (msg, opts) => addToast('warning', msg, opts),
    info:    (msg, opts) => addToast('info', msg, opts),
  });

  useEffect(() => {
    api.current.success = (msg, opts) => addToast('success', msg, opts);
    api.current.error   = (msg, opts) => addToast('error', msg, opts);
    api.current.warning = (msg, opts) => addToast('warning', msg, opts);
    api.current.info    = (msg, opts) => addToast('info', msg, opts);
  }, [addToast]);

  const containerStyle = {
    position: 'fixed',
    top: '16px',
    right: '16px',
    zIndex: 999999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    pointerEvents: 'none',
  };

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      {ReactDOM.createPortal(
        <div className="toast-container-portal" style={containerStyle}>
          {toasts.map((t) => (
            <Toast key={t.id} toast={t} onClose={removeToast} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
